import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DoseEvent, MedicineItemType } from "@/types/pillbox";
import { Reminders } from "@/types/reminders";
import { formatMedicineTitle } from "../utils/formatMedicine";
import MedicineCard from "./MedicineCard";
import styles from "./PeriodSection.module.css";
import { DaytimeSlot } from "@/types/pillbox";

export interface PeriodSectionProps {
  period: DaytimeSlot;
  icon: React.ReactNode;
  doses: DoseEvent[];
  onSelectDose?: (dose: DoseEvent) => void;
  onEditDose?: (dose: DoseEvent) => void;
  onStopDose?: (dose: DoseEvent) => void;
  /** Returns a Reminders-shaped prefill for this dose's medicine, or null
   *  when it doesn't qualify (e.g. every-N-hours dosing) — gates whether
   *  "Add Reminder" shows in the card's options menu at all. */
  getReminderPrefillForDose?: (dose: DoseEvent) => Partial<Reminders> | null;
  /** Returns the reminder already set for this dose's medicine, if any —
   *  shows its schedule inline and swaps "Add Reminder" to "Edit Reminder". */
  getReminderForDose?: (dose: DoseEvent) => Reminders | null;
  /** Whether the CURRENT (viewing) member has this dose's medicine on
   *  their own shopping list. */
  getInShoppingListForDose?: (dose: DoseEvent) => boolean;
  onToggleShoppingListDose?: (dose: DoseEvent) => void;
  /** DOM id — scroll target when its daytime-slot card is tapped above. */
  id?: string;
  /** Briefly plays a highlight animation (see .highlighted in the CSS). */
  highlighted?: boolean;
}

const PERIOD_TINT: Record<DaytimeSlot, string> = {
  morning: "#fcf7ec",
  afternoon: "#fdf4ec",
  evening: "#f9ecf4",
  night: "#edf3fc",
};

const PeriodSection: React.FC<PeriodSectionProps> = ({
  period,
  icon,
  doses,
  onSelectDose,
  onEditDose,
  onStopDose,
  getReminderPrefillForDose,
  getReminderForDose,
  getInShoppingListForDose,
  onToggleShoppingListDose,
  id,
  highlighted,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      id={id}
      className={`${styles.container} ${highlighted ? styles.highlighted : ""}`}
      style={{ background: PERIOD_TINT[period] }}
    >
      <div className={styles.header}>
        <span className={styles.iconBox}>{icon}</span>
        <span className={styles.label}>{t(`pillbox.daytime.${period}`)}</span>
      </div>
      {doses.length === 0 ? (
        <div className={styles.emptyCard}>
          <span className={styles.emptyText}>
            {t("pillbox.nothingScheduled")}
          </span>
        </div>
      ) : (
        <div className={styles.doseList}>
          {doses.map((dose) => {
            const reminderPrefill = getReminderPrefillForDose?.(dose) ?? null;
            const reminder = getReminderForDose?.(dose) ?? null;
            return (
              <MedicineCard
                key={`${dose.itemId}-${dose.time}`}
                itemType={dose.itemType as MedicineItemType}
                title={formatMedicineTitle(dose)}
                trailingIcon="dots"
                onClick={onSelectDose ? () => onSelectDose(dose) : undefined}
                onEditDetails={onEditDose ? () => onEditDose(dose) : undefined}
                reminder={reminder}
                onAddReminder={
                  reminderPrefill
                    ? () =>
                        navigate("/reminders/medication", {
                          state: { pillboxPrefill: reminderPrefill },
                        })
                    : undefined
                }
                onEditReminder={
                  reminder
                    ? () =>
                        navigate("/reminders/medication", {
                          state: { reminder },
                        })
                    : undefined
                }
                inShoppingList={getInShoppingListForDose?.(dose)}
                showShoppingListBadge={false}
                onToggleShoppingList={
                  onToggleShoppingListDose
                    ? () => onToggleShoppingListDose(dose)
                    : undefined
                }
                onStopMedication={
                  onStopDose ? () => onStopDose(dose) : undefined
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PeriodSection;
